import type { utils } from 'blockly/core';

import { categoryCssConfig, categorySubCssConfig } from '../categoryCssConfig.js';
import { BEGINNER_SOUND_COLOR } from './constants.js';

type ToolboxItemInfo = utils.toolbox.ToolboxItemInfo;

/** Boards that include built-in speaker / microphone beginner blocks. */
const BOARDS_WITH_AUDIO_DEVICES = new Set(['esp32-devkit', 'skyrover-esp32-s3']);

export function boardSupportsBeginnerAudioDevices(boardId: string): boolean {
  return BOARDS_WITH_AUDIO_DEVICES.has(boardId);
}

/** Beginner Sound category — buzzers always; speaker/mic only on supported boards. */
export function buildBeginnerSoundCategory(boardId = ''): ToolboxItemInfo {
  const contents: ToolboxItemInfo[] = [
    {
      kind: 'category',
      name: 'Active Buzzer',
      colour: String(BEGINNER_SOUND_COLOR),
      cssconfig: categorySubCssConfig('active-buzzer'),
      contents: [
        { kind: 'block', type: 'beginner_active_buzzer_turn' },
        { kind: 'block', type: 'beginner_active_buzzer_beep' },
        { kind: 'block', type: 'beginner_active_buzzer_beep_repeatedly' },
      ],
    },
    {
      kind: 'category',
      name: 'Passive Buzzer',
      colour: String(BEGINNER_SOUND_COLOR),
      cssconfig: categorySubCssConfig('passive-buzzer'),
      contents: [
        { kind: 'block', type: 'beginner_passive_buzzer_play_note' },
        { kind: 'block', type: 'beginner_passive_buzzer_play_melody' },
        { kind: 'block', type: 'beginner_passive_buzzer_stop' },
      ],
    },
  ];

  if (boardSupportsBeginnerAudioDevices(boardId)) {
    contents.push(
      {
        kind: 'category',
        name: 'Speaker',
        colour: String(BEGINNER_SOUND_COLOR),
        cssconfig: categorySubCssConfig('speaker'),
        contents: [
          { kind: 'block', type: 'beginner_speaker_play_sound' },
          { kind: 'block', type: 'beginner_speaker_pause' },
          { kind: 'block', type: 'beginner_speaker_resume' },
          { kind: 'block', type: 'beginner_speaker_stop' },
          { kind: 'block', type: 'beginner_speaker_set_volume' },
        ],
      },
      {
        kind: 'category',
        name: 'Microphone',
        colour: String(BEGINNER_SOUND_COLOR),
        cssconfig: categorySubCssConfig('microphone'),
        contents: [
          { kind: 'block', type: 'beginner_mic_is_loud' },
          { kind: 'block', type: 'beginner_mic_sound_level' },
          { kind: 'block', type: 'beginner_mic_wait_loud' },
          { kind: 'block', type: 'beginner_mic_when_loud' },
        ],
      },
    );
  }

  return {
    kind: 'category',
    name: 'Sound',
    colour: String(BEGINNER_SOUND_COLOR),
    cssconfig: categoryCssConfig('sound'),
    contents,
  };
}
